import { Button } from '@chakra-ui/react'
import { faDownload } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Document, Page, PDFDownloadLink } from '@react-pdf/renderer'
import { DateTime } from 'luxon'

import initPdfFonts from '@/components/PDF/initPdfFonts'
import BrowserOnly from '@/components/UI/BrowserOnly'
import Tippy from '@/components/UI/Tippy'

import { Activities } from './PDF/Activities'
import { Header } from './PDF/Header'
import type { VolunteerActivityComponentAttributes } from './types'

export type DownloadPDFButtonProps = {
  activities: VolunteerActivityComponentAttributes[]
  userFullName: string
  memberSince: DateTime
  startDate: DateTime | null
  endDate: DateTime | null
  totalTime: string
}

initPdfFonts()

function DownloadButtonInternal({ disabled }: { disabled: boolean }): JSX.Element {
  return (
    <Button isDisabled={disabled} leftIcon={<FontAwesomeIcon icon={faDownload} />}>
      Download PDF
    </Button>
  )
}

export function DownloadPDFButton({
  activities,
  userFullName,
  startDate,
  endDate,
  memberSince,
  totalTime,
}: DownloadPDFButtonProps): JSX.Element | null {
  const allActivitiesApproved = activities.every(activity => activity.approved_at != null)

  const startDateString =
    startDate == null
      ? memberSince.toLocaleString(DateTime.DATE_SHORT)
      : startDate.toLocaleString(DateTime.DATE_SHORT)
  const endDateString =
    endDate == null
      ? DateTime.local().toLocaleString(DateTime.DATE_SHORT)
      : endDate.toLocaleString(DateTime.DATE_SHORT)

  const volunteerHoursPdf = (
    <Document>
      <Page style={{ paddingHorizontal: '40px', paddingVertical: '32px' }} wrap>
        <Header
          fullName={userFullName}
          startDateString={startDateString}
          endDateString={endDateString}
          totalTime={totalTime}
        />
        <Activities
          activities={activities}
          startDateString={startDateString}
          endDateString={endDateString}
          totalTime={totalTime}
        />
      </Page>
    </Document>
  )

  if (!allActivitiesApproved) {
    return (
      <Tippy content='You may only download approved activities. Please set the "Status" filter to "Approved" and try again.'>
        <span>
          <DownloadButtonInternal disabled />
        </span>
      </Tippy>
    )
  }

  return (
    <BrowserOnly>
      <PDFDownloadLink
        document={volunteerHoursPdf}
        style={{ textDecoration: 'none' }}
        fileName="shw-volunteer-hours.pdf"
      >
        {({ loading, error }) => (
          <Tippy
            content={
              error != null
                ? `Error loading PDF: ${error}`
                : loading
                  ? 'Loading PDF'
                  : 'Download a signed PDF of your verified volunteer hours'
            }
          >
            <span>
              <DownloadButtonInternal disabled={loading || error != null} />
            </span>
          </Tippy>
        )}
      </PDFDownloadLink>
    </BrowserOnly>
  )
}
